import { createRouter } from '@tanstack/react-router';
import type { AnyRoute } from '@tanstack/react-router';
import { providerQueryClient } from './services/apiClient';
import NotFoundPage from './components/NotFoundPage';
import ErrorPage from './components/ErrorPage';

/**
 * Builds the application router.
 *
 * Pending/preload tuning (matters most for the loader-driven help-center
 * routes — /docs, /developers, /about-legal — which fetch markdown in their
 * loaders):
 *
 * - `defaultPreload: 'intent'` runs a route's loader on hover/touch, warming
 *   the content cache before the click so most first navigations resolve
 *   instantly with no fallback shown.
 * - `defaultPendingMs: 150` swaps the old page for the route's pendingComponent
 *   quickly once a load is genuinely in flight, instead of leaving stale
 *   content on screen (TanStack's default is 1000ms, long enough that a cold
 *   markdown fetch finishes first and the skeleton never appears).
 * - `defaultPendingMinMs: 300` holds the fallback long enough to avoid a
 *   flash-and-vanish on borderline loads.
 *
 * Generic over the route tree so callers keep a concrete, fully-typed router
 * (preserving `<Link to>` checking); the factory shape also lets tests build a
 * router from a stub tree without importing the generated route tree.
 */
export function createAppRouter<TRouteTree extends AnyRoute>(routeTree: TRouteTree) {
  return createRouter({
    routeTree,
    context: {
      queryClient: providerQueryClient,
    },
    defaultNotFoundComponent: NotFoundPage,
    defaultErrorComponent: ErrorPage,
    defaultPreload: 'intent',
    defaultPendingMs: 150,
    defaultPendingMinMs: 300,
  });
}
